import React, { Fragment } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { compact, unique, without } from 'utilities/array'
import { omit } from 'utilities/object'
import { extractNumber } from 'utilities/number'
import { accentuate, codify } from './'
import {
    Section, Heading,
    TableColumnHeader, TableRow, TableRowHeader, TableCell,
    CodeLine, Disclaimer
} from './s'
import Paragraph from 'components/typography/paragraph'
import { Required } from 'components/form/field/s'
import { Table, TableHead, TableBody } from 'components/table'
import Tooltip from 'components/tooltip'
import { LinkSquare as Identity } from 'styled-icons/fluentui-system-regular'

const IntegrationSupportedFields = ({ type, name, scopes, scope, salt }) => {
    const { formatMessage } = useIntl()

    if(!scope) {
        return null
    }

    const { supportedFields } = scopes[scope]
    if(!supportedFields?.length) {
        return null
    }

    const heading = getHeadingForScope(scope)
    const preamble = getPreambleForScope(scope)

    const mapsToSources = supportedFields.some(({ sources }) => !!sources?.length)

    return (
        <Section>
            <Heading>
                <FormattedMessage {...heading} />
            </Heading>
            {!!preamble && (
                <Paragraph>
                    <FormattedMessage
                        {...preamble}
                        values={{ integration: name }} />
                </Paragraph>
            )}
            <Table>
                <TableHead>
                    <TableRow>
                        <TableColumnHeader>
                            <FormattedMessage
                                id="noun_huma_field"
                                defaultMessage="Huma field" />
                        </TableColumnHeader>
                        <TableColumnHeader>
                            <FormattedMessage
                                id={mapsToSources ?
                                    'noun_external_field' :
                                    'noun_json_key'
                                }
                                defaultMessage={mapsToSources ?
                                    'External field' :
                                    'JSON key'
                                } />
                        </TableColumnHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {supportedFields
                        .sort(({ referenceId: one }, { referenceId: two }) => !!two - !!one)
                        .map(({
                            humaName, sources = [],
                            required, unique: requiredToBeUnique = false,
                            referenceId, customDisclaimer = false,
                            limitations = [], validations = []
                        }) => {
                            if(scope !== 'userProfile') {
                                humaName = `${scope}:${humaName}`
                            }

                            let label = getLabelForField(humaName)

                            if(label) {
                                label = formatMessage(label)
                            } else {
                                label = humaName
                            }

                            const generalValidation = validations.find(({ name }) => !name?.length)
                            const specificValidations = validations.filter(({ name }) => !!name?.length)
                            sources = without(sources, unique(specificValidations.flatMap(({ name }) => name)))

                            const fieldDisclaimers = compact([
                                ...(generalValidation?.validations.map(getValidationDisclaimerForField) ?? []),
                                ...limitations.map(getLimitationDisclaimerForField),
                                !!customDisclaimer && getCustomDisclaimerForField(`custom:${type}:${humaName}`),
                                !!requiredToBeUnique && getCustomDisclaimerForField(`custom:unique:integration`),
                                (sources.includes('salaryInformation.salaryType') && type === 'visma.net') && getCustomDisclaimerForField(`custom:visma.net:salary:periodUnit:salaryType`),
                                (sources.includes('salaryInformation.contractSalaryType') && type === 'visma.net') && getCustomDisclaimerForField(`custom:visma.net:salary:periodUnit:contractSalaryType`),
                                ['email', 'phone', 'employmentId'].includes(humaName) && getCustomDisclaimerForField(`custom:unique:huma`)
                            ])

                            const key = `${salt}:supported-field:${humaName}`

                            return (
                                <TableRow key={key}>
                                    <TableRowHeader>
                                        {!!referenceId && (
                                            <Tooltip
                                                content={formatMessage({
                                                    id: 'integration_field_reference_explainer',
                                                    defaultMessage: '{name} is used to find matches between Huma and {service}'
                                                }, {
                                                    name: <strong>{label}</strong>,
                                                    service: name
                                                })}
                                                placement="top-start">
                                                <Identity size={24} />
                                            </Tooltip>
                                        )}
                                        {label}
                                        {required && (
                                            <Required>
                                                {`* (${formatMessage({
                                                    id: 'label_required',
                                                    defaultMessage: 'Required'
                                                }).toUpperCase()})`}
                                            </Required>
                                        )}
                                    </TableRowHeader>
                                    <TableCell>
                                        {specificValidations.map(({ name: names, validations }) => {
                                            const specificValidationKey = `${key}:source-with-validation:${names.join('+')}`

                                            return (
                                                <Fragment key={specificValidationKey}>
                                                    {names.map(name => (
                                                        <CodeLine key={`${specificValidationKey}:name:${name}`}>
                                                            {name}
                                                        </CodeLine>
                                                    ))}
                                                    {validations.map(validation => {
                                                        const disclaimer = getValidationDisclaimerForField(validation)
                                                        if(!disclaimer) {
                                                            return null
                                                        }

                                                        return (
                                                            <Disclaimer
                                                                className="compact source-validation"
                                                                key={`${specificValidationKey}:validation:${validation}`}>
                                                                <FormattedMessage
                                                                    {...omit(disclaimer, 'values')}
                                                                    values={{
                                                                        ...disclaimer?.values,
                                                                        service: name,
                                                                        accent: accentuate
                                                                    }} />
                                                            </Disclaimer>
                                                        )
                                                    })}
                                                </Fragment>
                                            )
                                        })}
                                        {sources.map(source => (
                                            <CodeLine key={`${key}:source:${source}`}>
                                                {source}
                                            </CodeLine>
                                        ))}
                                        {(!specificValidations.length && !sources.length) && (
                                            <CodeLine>
                                                {humaName}
                                            </CodeLine>
                                        )}
                                        {fieldDisclaimers.map(disclaimer => (
                                            <Disclaimer
                                                className="preserve-line-breaks compact"
                                                key={`${key}:disclaimer:${disclaimer.id}`}>
                                                <FormattedMessage
                                                    {...omit(disclaimer, 'values')}
                                                    values={{
                                                        ...disclaimer?.values,
                                                        service: name,
                                                        accent: accentuate,
                                                        code: codify
                                                    }} />
                                            </Disclaimer>
                                        ))}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                </TableBody>
            </Table>
        </Section>
    )
}

export const getHeadingForScope = scope => ({
    userProfile: {
        id: 'integration_heading_supported_fields_user',
        defaultMessage: 'Supported user fields'
    },
    salary: {
        id: 'integration_heading_supported_fields_salary',
        defaultMessage: 'Supported salary fields'
    },
    absence: {
        id: 'integration_heading_supported_fields_absence',
        defaultMessage: 'Supported absence fields'
    }
})[scope] ?? {
    id: 'integration_heading_supported_fields',
    defaultMessage: 'Supported fields'
}

export const getPreambleForScope = scope => ({
    userProfile: {
        id: 'integration_preamble_supported_fields_user',
        defaultMessage: 'These are the profile fields that will be synchronized between Huma and {integration}:'
    },
    salary: {
        id: 'integration_preamble_supported_fields_salary',
        defaultMessage: 'These are the salary fields that will be synchronized between Huma and {integration}:'
    },
    absence: {
        id: 'integration_preamble_supported_fields_absence',
        defaultMessage: 'These are the absence fields that will be synchronized between Huma and {integration}:'
    }
})[scope] ?? null

export const getLabelForField = key => ({
    address: {
        id: 'person_label_address_home',
        defaultMessage: 'Address'
    },
    avatarUrl: {
        id: 'person_label_profile_image',
        defaultMessage: 'Profile image'
    },
    bankAccount: {
        id: 'person_label_financial_accountnumber',
        defaultMessage: 'Bank account number'
    },
    birthDate: {
        id: 'person_label_birthdate',
        defaultMessage: 'Date of birth'
    },
    civilStatus: {
        id: 'person_label_civilstatus',
        defaultMessage: 'Civil status'
    },
    country: {
        id: 'address_label_country',
        defaultMessage: 'Country'
    },
    email: {
        id: 'person_label_emailaddress',
        defaultMessage: 'Email address'
    },
    privateEmail: {
        id: 'person_label_email_personal',
        defaultMessage: 'Personal email address'
    },
    employmentId: {
        id: 'person_label_employment_id',
        defaultMessage: 'Employment ID'
    },
    employmentType: {
        id: 'person_label_employment_type',
        defaultMessage: 'Employment type'
    },
    employmentPercentage: {
        id: 'person_label_employment_percentage',
        defaultMessage: 'Employment percentage'
    },
    familyName: {
        id: 'person_label_name_family',
        defaultMessage: 'Family name'
    },
    firstDayOfWork: {
        id: 'person_label_employment_date_start',
        defaultMessage: 'First day of work'
    },
    gender: {
        id: 'person_label_gender',
        defaultMessage: 'Gender'
    },
    givenName: {
        id: 'person_label_name_given',
        defaultMessage: 'Given name'
    },
    employmentStartDate: {
        id: 'person_label_employment_contract_date_start',
        defaultMessage: 'Employment start date'
    },
    id: {
        id: 'person_label_user_id',
        defaultMessage: 'User ID'
    },
    identifications: {
        id: 'person_pane_about_section_identifications',
        defaultMessage: 'Identifications'
    },
    jobTitle: {
        id: 'person_label_employment_jobtitle',
        defaultMessage: 'Job Title'
    },
    lastDayOfWork: {
        id: 'person_label_employment_date_end',
        defaultMessage: 'Last day of work'
    },
    nationality: {
        id: 'person_label_nationality',
        defaultMessage: 'Nationality'
    },
    phone: {
        id: 'person_label_phonenumber',
        defaultMessage: 'Phone number'
    },
    probationEndDate: {
        id: 'person_label_employment_probation_date_end',
        defaultMessage: 'Probation end date'
    },
    employmentEndDate: {
        id: 'person_label_employment_termination_date',
        defaultMessage: 'Employment end date'
    },
    terminationNoticeDate: {
        id: 'person_label_employment_termination_notice_date',
        defaultMessage: 'Termination notice date'
    },

    'salary:fromDate': {
        id: 'date_label_from',
        defaultMessage: 'From date'
    },
    'salary:toDate': {
        id: 'date_label_to',
        defaultMessage: 'To date'
    },
    'salary:amount': {
        id: 'noun_salary_amount',
        defaultMessage: 'Salary amount'
    },
    'salary:periodUnit': {
        id: 'salary_label_frequency',
        defaultMessage: 'Interval'
    },

    'absence:type': {
        id: 'noun_absence_type',
        defaultMessage: 'Absence type'
    },
    'absence:dates': {
        id: 'absence_type_label_time_period',
        defaultMessage: 'Time period'
    },
    'absence:grade': {
        id: 'noun_percentage',
        defaultMessage: 'Percentage'
    },
    'absence:note': {
        id: 'absence_label_notes',
        defaultMessage: 'Notes'
    }
})[key] ?? null

const getLimitationDisclaimerForField = limitation => {
    limitation.type = limitation.type.toLowerCase()
    limitation.target = limitation.target.toLowerCase()

    if(limitation.type === 'create') {
        if(limitation.target === 'huma') {
            return {
                id: 'integration_supported_field_limitation_create_huma',
                defaultMessage: 'This field can’t be set in Huma with the value in {service}.'
            }
        }

        return {
            id: 'integration_supported_field_limitation_create_integration',
            defaultMessage: 'This field can’t be set in {service} with the value in Huma.'
        }
    }

    if(limitation.type === 'update') {
        if(limitation.target === 'huma') {
            return {
                id: 'integration_supported_field_limitation_update_huma',
                defaultMessage: 'This field can’t be updated in Huma with the value in {service}.'
            }
        }

        return {
            id: 'integration_supported_field_limitation_update_integration',
            defaultMessage: 'This field can’t be updated in {service} with the value in Huma.'

        }
    }

    if(limitation.type === 'delete') {
        if(limitation.target === 'huma') {
            return {
                id: 'integration_supported_field_limitation_delete_huma',
                defaultMessage: 'This field can’t be unset in Huma when the value in {service} is removed.'
            }
        }

        return {
            id: 'integration_supported_field_limitation_delete_integration',
            defaultMessage: 'This field can’t be unset in {service} when the value in Huma is removed.'
        }
    }

    return null
}

const getValidationDisclaimerForField = key => {
    if(!key) {
        return null
    }

    key = key.toLowerCase()

    if(key.startsWith('max_length')) {
        return {
            id: 'integration_validation_max_length',
            defaultMessage: 'Max. length {count} characters.',
            values: {
                count: extractNumber(key)
            }
        }
    }

    return {
        bank_account_norwegian: {
            id: 'person_input_helper_financial_accountnumber_norwegian',
            defaultMessage: 'Must be a valid Norwegian bank account number.'
        },
        bank_account_international: {
            id: 'integration_bankAccount_international_format',
            defaultMessage: 'Will be filled if the bank account number is in international format (IBAN).'
        },
        bank_account_swedish: {
            id: 'person_input_helper_financial_accountnumber_swedish',
            defaultMessage: 'Must be a valid Swedish bank account number.'
        }
    }[key] ?? null
}

const getCustomDisclaimerForField = key => ({
    'custom:24sevenoffice:employmentStartDate': {
        id: 'integration_24sevenoffice_employment_dates_disclaimer',
        defaultMessage: 'When updating employment start or end in Huma, if there are multiple employment periods in 24SevenOffice, the updates are synced to the primary employment period. If there are no employment periods in 24SevenOffice, one is created and set as primary.'
    },
    'custom:24sevenoffice:identifications': {
        id: 'integration_24sevenoffice_identifications_disclaimer',
        defaultMessage: '24SevenOffice supports only one identification. If there are multiple identifications on the Huma profile, the first in the list will be synchronized.'
    },
    'custom:azure:address': {
        id: 'integration_azure_address_iso_requirement',
        defaultMessage: 'Country will only be mapped to Huma if it’s a valid ISO 3166-1 alpha-2 country code in Azure Active Directory.'
    },
    'custom:cronalon:address': {
        id: 'integration_cronalon_address_iso_requirement',
        defaultMessage: 'Country will only be mapped to Huma if it’s a valid ISO 3166-1 alpha-2 country code in Crona Lön.'
    },
    'custom:netvisor:bankAccount': {
        id: 'integration_netvisor_bankAccount_format',
        defaultMessage: 'Netvisor only accepts bank account numbers in international format (IBAN).'
    },
    'custom:netvisor:birthDate': {
        id: 'integration_netvisor_derived_value',
        defaultMessage: 'In Netvisor, this field’s value is derived from the identification number.'
    },
    'custom:netvisor:gender': {
        id: 'integration_netvisor_derived_value',
        defaultMessage: 'In Netvisor, this field’s value is derived from the identification number.'
    },
    'custom:netvisor:identifications': {
        id: 'integration_netvisor_identifications_limitation',
        defaultMessage: 'When migrating users from Huma to Netvisor, Finnish Personal Identity Code (HETU) is required.\nWhen syncing users from Netvisor to Huma, personal identification numbers of other countries are also accepted.'
    },
    'custom:netvisor:nationality': {
        id: 'integration_netvisor_nationality_value',
        defaultMessage: 'Nationality from Huma will be used if provided, otherwise Finnish is used as default.'
    },
    'custom:nuu:phone': {
        id: 'integration_nuu_phone_format',
        defaultMessage: 'Nuu requires this field to have a proper validation and country code.'
    },
    'custom:poweroffice:gender': {
        id: 'person_helper_gender_poweroffice',
        defaultMessage: 'PowerOffice currently only supports synchronizing <accent>male</accent> and <accent>female</accent> as genders. If the gender is unspecified in PowerOffice, it will remain unchanged in Huma. If the gender is <accent>custom</accent> in Huma, it will be unspecified in PowerOffice.'
    },
    'custom:poweroffice:identifications': {
        id: 'integration_poweroffice_identifications_limitation',
        defaultMessage: 'Only Norwegian personal identification numbers are supported in PowerOffice.'
    },
    'custom:tripletex:identifications': {
        id: 'integration_tripletex_identifications_limitation',
        defaultMessage: 'Only Norwegian personal identification numbers are supported in Tripletex.'
    },
    'custom:tripletex:salary:fromDate': {
        id: 'integration_tripletex_date_position_helper',
        defaultMessage: 'Huma will create or update the employee’s position based on this date.'
    },
    'custom:tripletex:salary:toDate': {
        id: 'integration_tripletex_date_position_helper',
        defaultMessage: 'Huma will create or update the employee’s position based on this date.'
    },
    'custom:tripletex:salary:amount': {
        id: 'integration_tripletex_amount_helper',
        defaultMessage: 'Annual or monthly amounts will be used to calculate the annual salary.\n\nHourly or weekly amounts will be calculated as an hourly wage based on the standard time for the pay period, as defined in Tripletex.'
    },
    'custom:tripletex:salary:periodUnit': {
        id: 'integration_tripletex_period_unit_helper',
        defaultMessage: 'If the salary is monthly or annual, the renumeration type will updated as Fixed Salary.\n\nIf the salary is hourly or weekly, the renumeration type will be updated as Hourly Pay.'
    },
    'custom:unimicro:employmentType': {
        id: 'integration_unimicro_employment_type_helper',
        defaultMessage: 'Only Permanent and Temporary employment type will be included in this integration.\n\nOnly the employment set as default in Unimicro will be updated.'
    },
    'custom:unimicro:employmentPercentage': {
        id: 'integration_unimicro_employment_helper',
        defaultMessage: 'Only the employment set as default in Unimicro will be updated.'
    },
    'custom:unimicro:employmentStartDate': {
        id: 'integration_unimicro_employment_helper',
        defaultMessage: 'Only the employment set as default in Unimicro will be updated.'
    },
    'custom:unimicro:jobTitle': {
        id: 'integration_unimicro_employment_helper',
        defaultMessage: 'Only the employment set as default in Unimicro will be updated.'
    },
    'custom:unimicro:gender': {
        id: 'integration_unimicro_gender_helper',
        defaultMessage: 'Unimicro currently only supports synchronizing <accent>male<\/accent> and <accent>female<\/accent> as genders. If the gender is unspecified in Unimicro, it will remain unchanged in Huma. If the gender is <accent>custom<\/accent> in Huma, it will be unspecified in Unimicro.'
    },
    'custom:visma.net:employmentId': {
        id: 'integration_visma.net_employment_id_cant_be_unset_helper',
        defaultMessage: 'Will only be updated in Visma.net for employees that are in draft mode.'
    },
    'custom:visma.net:identifications': {
        id: 'integration_visma.net_identifications_limitation',
        defaultMessage: 'Only Norwegian personal identification numbers are supported in Visma.net Payroll.'
    },
    'custom:visma.net:salary:periodUnit:salaryType': {
        id: 'integration_visma.net_period_unit_salary_type_helper',
        defaultMessage: '<code>Hourly</code> -> <code>Hourly</code>\n<code>Monthly, Yearly</code> -> <code>Period</code>\n\nWeekly salary in Huma will not be sent to Visma.net Payroll'
    },
    'custom:visma.net:salary:periodUnit:contractSalaryType': {
        id: 'integration_visma.net_period_unit_contract_salary_type_helper',
        defaultMessage: '<code>Hourly</code> -> <code>Hourly</code>\n<code>Monthly</code> -> <code>Monthly</code>\n<code>Yearly</code> -> <code>Yearly</code>'
    },
    'custom:visma.net:salary:fromDate': {
        id: 'integration_visma.net_date_helper',
        defaultMessage: 'If no value is set, date is set to <code>infinity</code> in Visma.net.'
    },
    'custom:visma.net:salary:toDate': {
        id: 'integration_visma.net_date_helper',
        defaultMessage: 'If no value is set, date is set to <code>infinity</code> in Visma.net.'
    },
    'custom:visma.net:salary:currency': {
        id: 'integration_visma.net_currency_helper',
        defaultMessage: 'Visma.net only accepts NOK – we won’t send salaries in other currencies.'
    },
    'custom:xledger:address': {
        id: 'integration_xledger_address_cant_be_unset_helper',
        defaultMessage: 'Will not be removed in Xledger by removing it in Huma. This needs to be handled manually in Xledger.'
    },
    'custom:xledger:bankAccount': {
        id: 'integration_xledger_bankAccount_address_not_sent',
        defaultMessage: 'Country will not be updated in Xledger when changed in Huma, only the other way around.'
    },
    'custom:xledger:identifications': {
        id: 'integration_xledger_identifications_limitation',
        defaultMessage: 'Only Norwegian personal identification numbers are supported in Xledger.'
    },
    'custom:unique:huma': {
        id: 'integration_field_disclaimer_required_to_be_unique_huma',
        defaultMessage: 'Huma requires this field’s value to be unique.'
    },
    'custom:unique:integration': {
        id: 'integration_field_disclaimer_required_to_be_unique_integration',
        defaultMessage: '{service} requires this field’s value to be unique.'
    }
})[key] ?? null

export default IntegrationSupportedFields